import * as React from 'react';
import { Label } from '@fluentui/react-label';
import type { ComponentProps, ComponentState, Slot } from '@fluentui/react-utilities';

export type SwitchSlots = {
  /**
   * The root element of the Switch.
   *
   * The root slot receives the `className` and `style` specified directly on the `<Switch>` tag.
   * All other native props will be applied to the primary slot: `input`.
   */
  root: NonNullable<Slot<'div'>>;

  /**
   * The track and the thumb sliding over it indicating the on and off status of the Switch.
   */
  indicator: NonNullable<Slot<'div'>>;

  /**
   * Hidden input that handles the Switch's functionality.
   *
   * This is the PRIMARY slot: all native properties specified directly on the `<Switch>` tag will be applied to this
   * slot, except `className` and `style`, which remain on the root slot.
   */
  input: NonNullable<Slot<'input'>>;

  /**
   * The Switch's label.
   */
  label?: Slot<typeof Label>;
};

export type SwitchOnChangeData = {
  checked: boolean;
};

/**
 * Switch Props
 */
export type SwitchProps = Omit<
  ComponentProps<Partial<SwitchSlots>, 'input'>,
  'checked' | 'defaultChecked' | 'onChange' | 'size'
> & {
  /**
   * Defines the controlled checked state of the Switch.
   * If passed, Switch ignores the `defaultChecked` property.
   * This should only be used if the checked state is to be controlled at a higher level and there is a plan to pass the
   * correct value based on handling `onChange` events and re-rendering.
   *
   * @default false
   */
  checked?: boolean;

  /**
   * When set, allows the Switch to be focusable even when it has been disabled. This is used in scenarios where it is
   * important to keep a consistent tab order for screen reader and keyboard users.
   *
   * @default false
   */
  disabledFocusable?: boolean;

  /**
   * Defines whether the Switch is initially in a checked state or not when rendered.
   *
   * @default false
   */
  defaultChecked?: boolean;

  /**
   * The position of the label relative to the Switch.
   *
   * @default after
   */
  labelPosition?: 'above' | 'after' | 'before';

  /**
   * The size of the Switch.
   *
   *  @default 'medium'
   */
  size?: 'small' | 'medium';

  /**
   * Callback to be called when the checked state value changes.
   */
  // eslint-disable-next-line @nx/workspace-consistent-callback-type -- can't change type of existing callback
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => void;
};

/**
 * Switch base props, excluding design-related props like size
 */
export type SwitchBaseProps = Omit<SwitchProps, 'size'>;

/**
 * State used in rendering Switch
 */
export type SwitchState = ComponentState<SwitchSlots> &
  Required<Pick<SwitchProps, 'disabledFocusable' | 'labelPosition' | 'size'>>;

/**
 * Switch base state, excluding design-related state like size
 */
export type SwitchBaseState = Omit<SwitchState, 'size'>;
